import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { SiteLayout } from "../components/site/SiteLayout";
import { useSeo, breadcrumbJsonLd } from "../lib/seo";
import { industries, services } from "../lib/content";
import { IndustryGlyph } from "../components/site/IndustryGlyph";
import { CTABlock } from "./ServicesPage";
import { Reveal } from "../lib/reveal";

export default function IndustryDetailPage() {
  const { slug } = useParams();
  const industry = industries.find((s) => s.slug === slug);
  useSeo({
    title: industry ? `${industry.title} — Industries` : "Industry",
    description: industry ? industry.summary : "",
    path: `/industries/${slug}`,
    jsonLd: industry && breadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: "Industries", path: "/industries" },
      { name: industry.title, path: `/industries/${slug}` },
    ]),
  });
  if (!industry) return <Navigate to="/industries" replace />;
  const idx = industries.findIndex((s) => s.slug === slug);
  const next = industries[(idx + 1) % industries.length];
  return (
    <SiteLayout>
      <section className="border-b border-border">
        <div className="container-wald py-12">
          <Link to="/industries" data-testid="industry-back" className="inline-flex items-center gap-2 text-sm opacity-70 hover:opacity-100">
            <ArrowLeft size={14} strokeWidth={2} /> All industries
          </Link>
        </div>
      </section>
      <section className="border-b border-border">
        <div className="container-wald py-16 md:py-24">
          <div className="grid grid-cols-12 gap-10 items-center">
            <div className="col-span-12 md:col-span-7">
              <div className="font-mono-tab text-xs opacity-60 mb-6">INDUSTRY / {industry.number}</div>
              <h1 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight">
                {industry.title}
              </h1>
              <p className="mt-6 opacity-85 max-w-xl leading-relaxed text-lg">{industry.summary}</p>
            </div>
            <div className="col-span-12 md:col-span-5">
              <div className="aspect-[4/3] border border-border bg-card p-6">
                <IndustryGlyph slug={slug} />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="border-b border-border">
        <div className="container-wald py-20">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="eyebrow mb-4">Focus areas</div>
              <h2 className="font-display text-2xl md:text-4xl tracking-tight max-w-md leading-[1.05]">
                Where we typically apply our stack.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7">
              <ul className="border-t border-border">
                {industry.focus.map((f) => (
                  <Reveal key={f} className="border-b border-border py-5 flex gap-4">
                    <Check size={18} className="text-[color:var(--wald-copper)] mt-0.5 shrink-0" strokeWidth={2} />
                    <span className="text-base leading-relaxed">{f}</span>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      <section className="border-b border-border bg-card">
        <div className="container-wald py-20">
          <div className="eyebrow mb-4">Applied disciplines</div>
          <h2 className="font-display text-2xl md:text-4xl tracking-tight mb-10 max-w-2xl leading-[1.05]">
            What we typically bring to {industry.title.toLowerCase()}.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-border">
            {services.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`industry-service-${s.slug}`}
                className="border-b border-r border-border p-6 group hover:bg-background transition-colors"
              >
                <div className="font-mono-tab text-xs opacity-50 mb-3">{s.number}</div>
                <div className="font-display text-lg tracking-tight mb-2">{s.title}</div>
                <p className="text-sm opacity-70 leading-relaxed">{s.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <section className="border-b border-border">
        <div className="container-wald py-12 flex justify-between items-center flex-wrap gap-4">
          <div className="opacity-60 text-sm">Continue</div>
          <Link to={`/industries/${next.slug}`} data-testid="industry-next" className="inline-flex items-center gap-3 group">
            <div className="text-right">
              <div className="eyebrow">Next industry</div>
              <div className="font-display text-xl tracking-tight">{next.title}</div>
            </div>
            <ArrowRight size={18} strokeWidth={2} className="group-hover:text-[color:var(--wald-copper)]" />
          </Link>
        </div>
      </section>
      <CTABlock />
    </SiteLayout>
  );
}
